// Contratos (schemas/DTOs) da fatia "storefront": clientes/fiado, cupons, configurações do
// catálogo e API pública do catálogo (vitrine, checkout, pedido/extrato por link).
import { z } from 'zod';
import { optionalText, paginationQuery, requiredText } from '../schemas/common';
import type { CatalogBanner } from '../schemas/catalog';
import type { PaymentMethodTypeKey } from '../settings';
import type { AccountEntryDTO, CustomerDTO } from '../types';

// ---------------------------------------------------------------- Clientes (lojista)

/** GET /customers/:id/orders */
export const customerOrdersQuery = paginationQuery.extend({
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

/** GET /customers/:id/account */
export const accountEntriesQuery = paginationQuery.extend({
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
});

/** GET /customers/birthdays?month=MM (padrão: mês atual no fuso da loja) */
export const birthdaysQuery = z.object({
  month: z.coerce.number().int().min(1, 'Mês inválido (use 1 a 12)').max(12, 'Mês inválido (use 1 a 12)').optional(),
});

/** POST /customers/:id/statement-link */
export const statementLinkSchema = z.object({
  /** Gera um novo link (o anterior deixa de funcionar). */
  regenerate: z.boolean().default(false),
});

export interface BirthdayCustomerDTO extends CustomerDTO {
  /** Dia do aniversário (1-31) */
  birthDay: number;
  /** Idade que completa neste ano (null se o ano de nascimento não for confiável) */
  age: number | null;
  isToday: boolean;
}

export interface CustomerImportResultDTO {
  created: number;
  skipped: number;
}

/** Resposta dos lançamentos na conta (recebimento, dívida manual, ajuste, estorno). */
export interface AccountEntryResultDTO {
  entry: AccountEntryDTO;
  customer: CustomerDTO;
}

export interface StatementLinkDTO {
  url: string;
  /** wa.me com mensagem pronta (saldo devedor + link do extrato) */
  whatsappUrl: string;
  balanceCents: number;
}

// ---------------------------------------------------------------- Catálogo (lojista)

export interface SlugAvailabilityDTO {
  /** Endereço normalizado (minúsculas, sem acentos) */
  slug: string;
  available: boolean;
  /** Alternativa livre quando o endereço pedido não pode ser usado */
  suggestion: string | null;
  /** Motivo quando indisponível (pt-BR) */
  message: string | null;
}

export interface UploadedImageDTO {
  id: string;
  url: string;
  thumbUrl: string | null;
  width: number | null;
  height: number | null;
  sizeBytes: number;
}

export interface CatalogLogoUploadDTO {
  logoUrl: string;
  media: UploadedImageDTO;
}

export interface CatalogBannerUploadDTO {
  banner: CatalogBanner;
  banners: CatalogBanner[];
  media: UploadedImageDTO;
}

export interface CustomDomainCheckDTO {
  domain: string;
  verified: boolean;
  verifiedAt: string | null;
  /** Destino esperado do CNAME (ex.: lojas.dezza.app) */
  target: string;
  /** IP para registro A (domínio raiz), quando configurado na plataforma */
  publicIp: string | null;
  found: { cname: string[]; a: string[] };
  message: string;
  /** Passo a passo para configurar o DNS (pt-BR) */
  instructions: string[];
}

// ---------------------------------------------------------------- Público (catálogo)

/** GET /public/resolve?host=&slug= */
export const resolveStoreQuery = z.object({
  host: z.string().trim().max(255).optional(),
  slug: z.string().trim().max(63).optional(),
});

export interface StoreResolveDTO {
  slug: string;
}

/** POST /public/stores/:slug/delivery-quote */
export interface DeliveryQuoteDTO {
  feeCents: number;
  label: string;
  estimate: string;
  /** Frete zerado por regra da loja (entrega grátis ou acima de X) */
  freeShippingApplied: boolean;
  /** Taxa a combinar com a loja (não entra no total) */
  toAgree: boolean;
}

export const STORE_REPORT_REASONS = ['scam', 'inappropriate', 'counterfeit', 'other'] as const;
export type StoreReportReason = (typeof STORE_REPORT_REASONS)[number];
export const STORE_REPORT_REASON_LABELS: Record<StoreReportReason, string> = {
  scam: 'Golpe ou fraude',
  inappropriate: 'Conteúdo impróprio',
  counterfeit: 'Produto falsificado/pirata',
  other: 'Outro motivo',
};

/** POST /public/stores/:slug/report — denúncia de loja */
export const storeReportSchema = z.object({
  reason: z.enum(STORE_REPORT_REASONS, { message: 'Escolha o motivo da denúncia' }),
  message: requiredText(2000, 'Conte o que aconteceu'),
  contact: optionalText(160),
  pageUrl: optionalText(500),
  /** Honeypot anti-bot: deve vir vazio. */
  website: z.string().max(0).optional(),
});
export type StoreReportInput = z.infer<typeof storeReportSchema>;

export interface CustomerStatementEntryDTO {
  id: string;
  type: 'charge' | 'payment' | 'adjustment';
  description: string;
  deltaCents: number;
  balanceAfterCents: number;
  paymentMethod: PaymentMethodTypeKey | null;
  orderNumber: number | null;
  /** Lançamento estornado pela loja */
  canceled: boolean;
  createdAt: string;
}

/** GET /public/statements/:token — extrato de fiado enviado ao cliente */
export interface CustomerStatementDTO {
  customer: { name: string; balanceCents: number; creditLimitCents: number | null };
  /** Movimentos dos últimos `periodDays` dias (mais recentes primeiro) */
  entries: CustomerStatementEntryDTO[];
  periodDays: number;
  store: {
    name: string;
    slug: string;
    logoUrl: string | null;
    whatsapp: string | null;
    primaryColor: string;
    catalogUrl: string;
  };
  /** Pix com o valor devido (quando há saldo devedor e a loja tem Pix configurado) */
  pix: { payload: string; amountCents: number } | null;
  /** Conversa com a loja no WhatsApp */
  whatsappUrl: string | null;
  generatedAt: string;
}
